import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, AfterViewChecked, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, forkJoin, map, Observable, of, startWith, switchMap } from 'rxjs';
import { ContentfulService } from '../../services/contentful.service';
import { CommonModule } from '@angular/common';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { environment } from '../../../environments/environment';
import { NzCardModule } from 'ng-zorro-antd/card';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { NzAnchorModule } from 'ng-zorro-antd/anchor';
import { AuthService } from '../../services/auth.service';
import { GtmService } from '../../services/gtm.service';

@Component({
    selector: 'app-landing-page',
    imports: [
        CommonModule,
        NzFlexModule,
        NzTypographyModule,
        NzButtonModule,
        NzDividerModule,
        NzIconModule,
        NzImageModule,
        NzFormModule,
        NzInputModule,
        NzSpaceModule,
        NzGridModule,
        NzLayoutModule,
        NzCardModule,
        NzAnchorModule,
    ],
      schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './landing-page.component.html',
    styleUrl: './landing-page.component.scss'
})
export class LandingPageComponent implements OnInit, AfterViewChecked {
  load = false;
  contentPage: any;
  isPhonePortrait = false;

  constructor(
    private contentfulService: ContentfulService,
    private responsive: BreakpointObserver,
    private auth: AuthService,
    private gtmService: GtmService,
    private translateService: TranslateService,
    private destroyRef: DestroyRef
  ) {}

ngOnInit(): void {
  const landingPageId = environment.contentful.landindPageId;
  this.translateService.onLangChange.pipe(
    map((event) => event.lang),
    startWith(this.translateService.currentLang || 'it'),
    distinctUntilChanged(),
    switchMap((locale) => this.contentfulService.getEntryIds(landingPageId).pipe(
      switchMap((blocks: any) => {
        const entries = Object.keys(blocks).map((key) => {
          const sysId = blocks[key]?.sys?.id;
          const block = sysId
            ? this.contentfulService.getEntry(sysId, locale).pipe(
                switchMap((value) => this.resolveContentfulValue(value.fields ?? value, locale)),
                map((fields) => ({ fields }))
              )
            : of(blocks[key]);
          return block.pipe(map((value) => [key, value] as const));
        });
        return entries.length ? forkJoin(entries).pipe(map((values) => Object.fromEntries(values))) : of({});
      })
    )),
    takeUntilDestroyed(this.destroyRef)
  ).subscribe((blocks) => {
    this.contentPage = blocks;
    this.load = true;
  });

  this.responsive.observe(Breakpoints.HandsetPortrait).subscribe((result) => {
    this.isPhonePortrait = false;
      if (result.matches) {
        this.isPhonePortrait = true;
      }
  });
  this.auth.isAuthenticatedSubject.next(false);
}


  ngAfterViewChecked() {
    const collection = document.getElementsByClassName('ant-anchor-ink');
    for (var i = 0; i < collection.length; i++) {
      collection[i].remove();
    }
  }

  private resolveContentfulValue(value: any, locale: string): Observable<any> {
    if (!value || typeof value !== 'object') {
      return of(value);
    }
    if (value.sys?.linkType === 'Entry') {
      return this.contentfulService.getEntry(value.sys.id, locale).pipe(
        switchMap((entry) => this.resolveContentfulValue(entry, locale))
      );
    }
    if (Array.isArray(value)) {
      return value.length ? forkJoin(value.map((item) => this.resolveContentfulValue(item, locale))) : of([]);
    }
    const entries = Object.keys(value).map((key) =>
      this.resolveContentfulValue(value[key], locale).pipe(map((resolved) => [key, resolved] as const))
    );
    return entries.length ? forkJoin(entries).pipe(map((resolved) => Object.fromEntries(resolved))) : of({});
  }

  _returnHtmlFromRichText(richText: any) {
    return documentToHtmlString(richText);
  }

  onSubscribeClick(fullTitle: string, uri: string) {
    const type = fullTitle.split(' ').pop();
    this.gtmService.pushSubscribeClickEvent(type);
    this.btnRedirect(uri);
  }

  btnRedirect(uri: string) {
    window.location.href = uri;
  }
}
